import { io, Socket } from 'socket.io-client';
import { api } from './api';
import { useNova } from './store';
import type { NovaEvent, Snapshot } from './types';

let socket: Socket | null = null;
let onRevoked: ((message: string) => void) | undefined;

/** Called when the session stops being valid: deactivated by an admin, or expired. */
export function whenRevoked(fn: (message: string) => void): void {
  onRevoked = fn;
}
let resyncing = false;

/** Re-reads everything from the server; used on connect and after a sequence gap. */
export async function resync(): Promise<void> {
  if (resyncing) return;
  resyncing = true;
  try {
    const snap = await api<Snapshot>('GET', '/snapshot');
    useNova.getState().applySnapshot(snap);
  } finally {
    resyncing = false;
  }
}

export function connectRealtime(token: string): void {
  disconnectRealtime();
  const store = useNova.getState();
  store.set({ link: 'connecting' });
  socket = io({ path: '/socket.io', auth: { token }, transports: ['websocket'], reconnectionDelayMax: 5000 });

  socket.on('ready', () => {
    useNova.getState().set({ link: 'live' });
    void resync();
  });
  socket.on('disconnect', () => useNova.getState().set({ link: 'reconnecting' }));
  socket.io.on('reconnect_attempt', () => useNova.getState().set({ link: 'reconnecting' }));
  socket.on('auth_error', (e?: { message?: string }) => onRevoked?.(e?.message ?? 'Your session ended. Sign in again.'));
  socket.on('event', (e: NovaEvent) => {
    const me = useNova.getState().user?.sub;
    if (e.type === 'agent.state_changed' && e.data.userId === me && (e.data.reason === 'deactivated' || e.data.reason === 'signed_out')) {
      onRevoked?.(e.data.reason === 'deactivated' ? 'Your account was deactivated by an admin' : 'A supervisor signed you out. Sign in again.');
      return;
    }
    const result = useNova.getState().applyEvent(e);
    if (result === 'gap') void resync();
  });
}

export function disconnectRealtime(): void {
  socket?.removeAllListeners();
  socket?.disconnect();
  socket = null;
}
